import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens, useId,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';
import type { AuthArgs } from '../../api/authArgs';
import {
  fetchRoutingModels, getRoutingModelSeedStatus,
  type RoutingModelFetchResult, type RoutingModelSeedStatus,
} from '../../api/configStoreClient';
import { RoutingModelChanges, RoutingModelDeployments } from './RoutingModelDeploymentDetails';
import { RoutingTpmThresholdControl, type RoutingTpmThresholdProps } from './RoutingTpmThresholdControl';
import { savedResources } from './routingModelConfiguration';

const useStyles = makeStyles({
  panel: {
    marginBottom: tokens.spacingVerticalS,
    minHeight: 0,
    minWidth: 0,
    maxWidth: '100%',
    maxHeight: '45%',
    overflow: 'auto',
    flexShrink: 1,
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingHorizontalM,
    overflowWrap: 'anywhere',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    '& h2': { margin: 0 },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  metadata: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  helper: {
    color: tokens.colorNeutralForeground2,
  },
  notice: {
    marginTop: tokens.spacingVerticalS,
  },
});

function FetchedTime({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <time dateTime={value} title={value}>
      {Number.isNaN(date.getTime()) ? value : date.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        second: '2-digit', timeZoneName: 'short',
      })}
    </time>
  );
}

function inventoryLabel(inventory: Pick<RoutingModelFetchResult, 'deploymentCount' | 'deployments' | 'inventoryComplete'>): string {
  const count = inventory.deploymentCount ?? inventory.deployments?.length;
  if (count === undefined) return 'Deployment count unavailable';
  return inventory.inventoryComplete === true
    ? `${count.toLocaleString()} total deployments`
    : `${count.toLocaleString()} saved deployments (partial inventory)`;
}

export function RoutingModelSeedSummary({ status }: { status: RoutingModelSeedStatus }) {
  const styles = useStyles();
  return (
    <>
      <div className={styles.metadata}>
        {status.available ? (
          <>
            <Badge appearance="outline" color="informative">{inventoryLabel(status)}</Badge>
            <Badge appearance="outline" color="informative">
              {status.endpointCount.toLocaleString()} eligible chat endpoints
            </Badge>
          </>
        ) : <Badge appearance="outline" color="informative">No usable saved seed</Badge>}
        <Text size={200} className={styles.helper}>
          Last fetched: {status.fetchedAtUtc ? <FetchedTime value={status.fetchedAtUtc} /> : 'Never'}
        </Text>
        {status.needsFetch && <Text size={200}>Fetch needed to update the routing seed.</Text>}
      </div>
      {status.available && status.inventoryComplete !== true && (
        <MessageBar intent="warning" className={styles.notice}>
          <MessageBarBody>
            Full inventory not confirmed. Legacy seeds include only Responses deployments; Fetch models again
            to discover all providers and model types. Existing routed endpoints remain usable.
          </MessageBarBody>
        </MessageBar>
      )}
    </>
  );
}

interface Props {
  environment: string;
  baseJson: string;
  environmentJson: string;
  baseETag: string | null;
  environmentETag: string | null;
  environmentExists: boolean;
  dirty: boolean;
  busy: boolean;
  fetching: boolean;
  getAuthArgs: () => Promise<AuthArgs | null>;
  onFetchStart: () => boolean;
  onFetchEnd: () => void;
  onSeedSaved: () => Promise<void>;
  threshold: RoutingTpmThresholdProps;
}

export function RoutingModelFetchPanel({
  environment, baseJson, environmentJson, baseETag, environmentETag, environmentExists,
  dirty, busy, fetching, getAuthArgs, onFetchStart, onFetchEnd, onSeedSaved, threshold,
}: Props) {
  const styles = useStyles();
  const headingId = useId('routing-models');
  const helperId = useId('routing-models-help');
  const disabledReasonId = useId('routing-models-disabled');
  const [status, setStatus] = useState<RoutingModelSeedStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [result, setResult] = useState<RoutingModelFetchResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lifetime = useRef(0);
  const statusRequest = useRef(0);
  const ownsFetch = useRef(false);
  const wasFetching = useRef(fetching);
  const options = useMemo(() => savedResources(baseJson, environmentJson), [baseJson, environmentJson]);

  useEffect(() => () => { lifetime.current += 1; statusRequest.current += 1; }, []);

  const refreshStatus = useCallback(async () => {
    const request = ++statusRequest.current;
    const current = () => statusRequest.current === request;
    setCheckingStatus(true);
    setStatusError(null);
    try {
      const auth = await getAuthArgs();
      if (!current()) return;
      if (!auth) throw new Error('Sign in to check the saved routing seed.');
      const next = await getRoutingModelSeedStatus(auth, environment);
      if (current()) setStatus(next);
    } catch (err) {
      if (current()) setStatusError(err instanceof Error ? err.message : 'Could not read the saved routing seed.');
    } finally {
      if (current()) setCheckingStatus(false);
    }
  }, [getAuthArgs, environment]);

  useEffect(() => {
    setConfirmOpen(false);
    setFetchError(null);
    setResult(null);
    void refreshStatus();
    return () => { statusRequest.current += 1; };
  }, [refreshStatus, baseETag, environmentETag, baseJson, environmentJson]);

  useEffect(() => {
    // If this tab was reopened during an older request, read fresh metadata after that request finishes.
    if (wasFetching.current && !fetching && !ownsFetch.current) {
      void Promise.all([refreshStatus(), onSeedSaved()]);
    }
    if (!fetching) ownsFetch.current = false;
    wasFetching.current = fetching;
  }, [fetching, refreshStatus, onSeedSaved]);

  const disabledReason = busy ? 'Wait for configuration loading or saving to finish.'
    : fetching ? 'A model fetch is already in progress.'
    : !environmentExists ? 'Save this environment override before fetching models.'
    : dirty ? 'Save or discard changes in Base and this environment before fetching. Fetch uses saved configuration only.'
    : options.error ? options.error
    : !options.enabled ? 'Model discovery is disabled in the saved configuration.'
    : checkingStatus ? 'Checking the saved seed…'
    : statusError || !status ? 'Seed status is unavailable. Check seed status before fetching.'
    : null;

  const doFetch = async () => {
    if (disabledReason || !onFetchStart()) return;
    ownsFetch.current = true;
    const started = lifetime.current;
    const current = () => lifetime.current === started;
    statusRequest.current += 1;
    setConfirmOpen(false);
    setFetchError(null);
    setResult(null);
    try {
      const auth = await getAuthArgs();
      if (!current()) return;
      if (!auth) throw new Error('Sign in to fetch routing models.');
      const fetched = await fetchRoutingModels(auth, { environment, baseETag, environmentETag });
      if (!current()) return;
      setResult(fetched);
      setStatus(null);
      // Refresh only the tree and seed metadata, never the editor or its selected layer/drafts.
      await Promise.all([refreshStatus(), onSeedSaved()]);
    } catch (err) {
      if (current()) setFetchError(err instanceof Error ? err.message : 'Fetch could not be confirmed. Check seed status before retrying.');
    } finally {
      onFetchEnd();
    }
  };

  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Text as="h2" id={headingId} size={400} weight="semibold">Model deployments and chat routing</Text>
          <Badge appearance="tint" color="brand" aria-label={`Environment: ${environment}`}>{environment}</Badge>
        </div>
        <div className={styles.actions}>
          <Button
            appearance="primary" icon={<ArrowDownload20Regular />} disabled={!!disabledReason}
            aria-describedby={disabledReason ? disabledReasonId : helperId}
            title={disabledReason ?? undefined} onClick={() => setConfirmOpen(true)}
          >
            Fetch models
          </Button>
          <Button
            appearance="secondary" icon={<ArrowClockwise20Regular />}
            disabled={fetching || checkingStatus || busy} onClick={() => void refreshStatus()}
          >
            Check seed status
          </Button>
        </div>
      </div>
      <Text id={helperId} size={200} block className={styles.helper}>
        Saved inventory, not live Azure status. Fetch discovers all deployed models across providers and model types
        in enabled accounts and replaces the saved seed. Only eligible chat endpoints enter routing.
        It does not edit this resources document or change the separate Skills embedding endpoint configuration or failover.
        The saved seed does not expire.
      </Text>
      <div aria-live="polite">
        {fetching ? <Spinner className={styles.notice} size="tiny" label="Fetching from Azure and saving the routing seed…" />
          : checkingStatus ? <Spinner className={styles.notice} size="tiny" label="Reading saved seed (no Azure query)…" /> : null}
        {status && !checkingStatus && !statusError && (
          <RoutingModelSeedSummary status={status} />
        )}
        {disabledReason && (
          <Text id={disabledReasonId} size={200} block className={styles.notice}>{disabledReason}</Text>
        )}
        {statusError && <MessageBar intent="error" className={styles.notice}><MessageBarBody>{statusError}</MessageBarBody></MessageBar>}
        {fetchError && (
          <MessageBar intent="error" className={styles.notice}>
            <MessageBarBody>
              {fetchError} A failed fetch does not replace the last known seed. If the response was lost, check seed status before retrying.
            </MessageBarBody>
          </MessageBar>
        )}
        {result && (
          <MessageBar intent={result.applied === false || result.inventoryComplete !== true ? 'warning' : 'success'} className={styles.notice}>
            <MessageBarBody>
              <strong>{result.applied === false ? 'Saved, runtime apply pending.' : 'Routing seed saved.'}</strong>
              {' '}{inventoryLabel(result)}; {result.endpointCount.toLocaleString()} eligible chat endpoints for <strong>{result.environment}</strong>
              {' '}at <FetchedTime value={result.fetchedAtUtc} />.
              {result.applied === false && ' Not applied to this runtime. Check seed status and the saved configuration.'}
              {result.inventoryComplete !== true && ' This response does not confirm a complete inventory. Fetch models again with an API that supports full-inventory discovery; existing routed endpoints remain usable.'}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
      <RoutingTpmThresholdControl
        {...threshold}
        runtimeExpected
        runtimeChecking={checkingStatus}
        runtimePercent={statusError ? undefined : status?.effectiveTpmFailoverThresholdPercent}
      />
      {result && <RoutingModelChanges changes={result.changes} />}
      {status && !checkingStatus && !statusError && <RoutingModelDeployments status={status} />}
      <Dialog open={confirmOpen} onOpenChange={(_, data) => setConfirmOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Fetch models for {environment}?</DialogTitle>
            <DialogContent>
              <Text style={{ display: 'block', marginBottom: 8 }}>
                Query Azure ARM using the <strong>saved Base + {environment}</strong> configuration below.
                Fetch includes all deployed models, regardless of provider or model type; routing uses only eligible chat endpoints.
                This replaces <strong>routing/model-deployments/default.{environment}.json</strong>, including
                removals of models no longer discovered. An empty inventory replaces the seed with zero deployments
                and zero chat endpoints. A nonempty inventory may still have no eligible chat endpoints.
                It does not deploy models, change quotas, edit the resources JSON, or change Skills embedding configuration or failover.
              </Text>
              <Text weight="semibold">Configured accounts ({options.accounts.filter((account) => account.enabled).length} enabled)</Text>
              <ul style={{ maxHeight: '35vh', overflowY: 'auto', paddingLeft: 20 }}>
                {options.accounts.map((account) => (
                  <li key={account.key} style={{ overflowWrap: 'anywhere', marginBottom: 8 }}>
                    <strong>{account.key}</strong>{account.enabled ? '' : ' — disabled, not queried'}
                    <br /><code>{account.resourceId}</code>
                  </li>
                ))}
              </ul>
              {!options.accounts.some((account) => account.enabled) && (
                <MessageBar intent="warning">
                  <MessageBarBody>No enabled accounts. Confirming will save an empty routing seed.</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button appearance="primary" disabled={!!disabledReason} onClick={() => void doFetch()}>Fetch models</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}
